import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Target, Sparkles, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth, isAdmin } from "../contexts/AuthContext";

/** Synergy demo — proves the integration end-to-end with 2021-22 trial data. */
export default function SynergyDemoPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.synergyDemo()
      .then(setData)
      .catch((e) => { setError(e.response?.data?.detail || e.message); toast.error("Synergy demo failed"); })
      .finally(() => setLoading(false));
  }, []);

  if (!isAdmin(user)) {
    return (
      <div className="min-h-screen"><Navbar /><main className="max-w-[900px] mx-auto px-6 py-12 text-center">
        <div className="text-lg text-white">Admin only</div>
      </main></div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8 space-y-8" data-testid="synergy-demo-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Sparkles className="w-5 h-5 text-[var(--org-accent)]" />
              <div className="text-[10px] uppercase tracking-[0.4em] text-[var(--org-accent)]">Synergy Basketball · Demo</div>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl leading-none tracking-tight text-white uppercase">
              {data?.player?.name || "Loading…"}
            </h1>
            <div className="mt-1 h-[2px] w-16 bg-[var(--org-accent)]" />
            <div className="mt-3 text-xs uppercase tracking-widest text-[#a0a0a0]">
              {data?.player?.team || "—"} · {data?.season?.name || "—"} · {data?.event_count ?? "—"} logged possessions
            </div>
          </div>
          <button onClick={() => navigate("/")}
            className="flex items-center gap-2 px-4 h-10 text-xs uppercase tracking-[0.25em] border border-white/15 text-[#a0a0a0] hover:border-[var(--org-accent)] hover:text-[var(--org-accent)]"
            style={{ transition: "border-color 0.15s ease, color 0.15s ease" }}
            data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>

        <div className="border border-[var(--org-accent)]/30 bg-[var(--org-accent)]/[0.04] p-4 text-xs text-[#a0a0a0] leading-relaxed">
          <span className="font-bold text-[var(--org-accent)] uppercase tracking-widest text-[10px]">Trial-only preview:</span>{" "}
          Synergy trial exposes just the 2021-22 ACC season, so this page is powered by Paolo Banchero (Duke) as a stand-in. When you upgrade to production access, the same integration will light up for every player on your roster.
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[var(--org-accent)]" /></div>
        ) : error ? (
          <div className="border border-red-500/30 bg-red-500/10 p-6 text-sm text-red-300">{error}</div>
        ) : data ? (
          <div className="grid grid-cols-1 lg:grid-cols-[520px_1fr] gap-6">
            <ShotChartCard shots={data.shot_chart} />
            <div className="space-y-6">
              <PlayTypeCard rows={data.play_types} />
              <ThreePointMixCard mix={data.three_point_mix} />
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
}

// ------- Shot chart --------------------------------------------------------
function ShotChartCard({ shots }) {
  // Synergy coord system: (0,0) at hoop, tenths of a foot; y > 0 is away from hoop,
  // y < 0 is behind the hoop, |x| < 250 is inside the sideline.
  // Map: chart 500x480. hoop drawn at cx=250, cy=60.
  const W = 500, H = 480;
  const HOOP_CY = 60;
  const scale = 0.85;  // tighten the visual so most of the halfcourt fills the box
  const toPx = (sx, sy) => ({
    cx: 250 + sx * scale,
    cy: HOOP_CY + sy * scale,
  });
  return (
    <div className="border border-white/10 bg-[#1c1c1c] p-4" data-testid="synergy-shot-chart">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-[var(--org-accent)]" />
          <div className="font-heading text-lg text-white uppercase tracking-tight">Shot Chart</div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[#757575]">{shots.length} shots</div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" data-testid="shot-chart-svg">
        {/* Court */}
        <rect x="0" y="0" width={W} height={H} fill="#1c1c1c" />
        <g stroke="#3a3a3a" strokeWidth="2" fill="none">
          {/* Baseline + sidelines */}
          <rect x="10" y="10" width={W - 20} height={H - 20} />
          {/* Paint (12 ft wide × 19 ft long → 120 × 190 at scale 1) */}
          <rect x={250 - 60 * scale} y={10} width={120 * scale} height={190 * scale} />
          {/* Free throw circle */}
          <circle cx="250" cy={10 + 190 * scale} r={60 * scale} />
          {/* 3PT arc — 23'9" from hoop; corners at ±22ft (drawn as an approximated arc). */}
          <path d={`M ${250 - 220 * scale} ${HOOP_CY}
                    L ${250 - 220 * scale} ${HOOP_CY + 100 * scale}
                    A ${237 * scale} ${237 * scale} 0 0 0 ${250 + 220 * scale} ${HOOP_CY + 100 * scale}
                    L ${250 + 220 * scale} ${HOOP_CY}`} />
          {/* Rim + backboard */}
          <line x1={250 - 30} y1={HOOP_CY - 15} x2={250 + 30} y2={HOOP_CY - 15} stroke="#d8b854" />
          <circle cx="250" cy={HOOP_CY} r="7" stroke="#d8b854" />
        </g>
        {/* Shots */}
        {shots.map((s, i) => {
          const { cx, cy } = toPx(s.x, s.y);
          if (cx < 0 || cx > W || cy < 0 || cy > H) return null;
          return (
            <circle key={i} cx={cx} cy={cy} r={s.kind === "3" ? 4.5 : 3.5}
              fill={s.made ? "#4ade80" : "none"}
              stroke={s.made ? "#4ade80" : "#f87171"}
              strokeWidth="1.6"
              fillOpacity="0.7" />
          );
        })}
      </svg>
      <div className="flex items-center gap-4 mt-3 text-[10px] uppercase tracking-widest">
        <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#4ade80]" /><span className="text-[#a0a0a0]">Made</span></div>
        <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full border border-[#f87171]" /><span className="text-[#a0a0a0]">Missed</span></div>
      </div>
    </div>
  );
}

// ------- Play-type table ---------------------------------------------------
function PlayTypeCard({ rows }) {
  const maxPoss = Math.max(...rows.map(r => r.possessions), 1);
  return (
    <div className="border border-white/10 bg-[#1c1c1c] p-4" data-testid="synergy-play-types">
      <div className="flex items-center gap-2 mb-3">
        <div className="font-heading text-lg text-white uppercase tracking-tight">Top 4 Play Types</div>
        <div className="text-[10px] uppercase tracking-widest text-[#757575]">by possessions</div>
      </div>
      <div className="space-y-2">
        {rows.map(r => (
          <div key={r.play_type} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 py-2 border-b border-white/5" data-testid={`playtype-${r.play_type}`}>
            <div>
              <div className="text-sm text-white font-bold">{humanPlayType(r.play_type)}</div>
              <div className="h-1 mt-1 bg-white/5 relative overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-[var(--org-accent)]/70" style={{ width: `${(r.possessions / maxPoss) * 100}%` }} />
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-sm text-white tabular-nums">{r.usage_pct}%</div>
              <div className="text-[9px] uppercase tracking-widest text-[#757575]">Usage</div>
            </div>
            <div className="text-right">
              <div className="font-mono text-sm text-[var(--org-accent)] tabular-nums">{r.ppp.toFixed(2)}</div>
              <div className="text-[9px] uppercase tracking-widest text-[#757575]">PPP</div>
            </div>
            <div className="text-right">
              <div className="font-mono text-xs text-[#a0a0a0] tabular-nums">{r.points}p · {r.possessions}poss</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const PLAY_TYPE_LABELS = {
  ISO: "Isolation", Iso: "Isolation",
  PostUp: "Post Up", Post_Up: "Post Up",
  PandRBallHandler: "P&R Ball-Handler",
  PandRRollMan: "P&R Roll Man",
  Cut: "Cut",
  Transition: "Transition",
  SpotUp: "Spot Up", Spot_Up: "Spot Up",
  OffensiveRebound: "Off. Rebound Putback",
  OffRebound: "Off. Rebound Putback",
  OffScreen: "Off-Screen", Off_Screen: "Off-Screen",
  HandOff: "Hand-Off", Hand_Off: "Hand-Off",
};
function humanPlayType(k) { return PLAY_TYPE_LABELS[k] || k; }

// ------- 3-point mix -------------------------------------------------------
function ThreePointMixCard({ mix }) {
  const total = mix.total_3pa || 0;
  const cs = mix.catch_and_shoot, od = mix.off_dribble;
  const cd = mix.catch_and_shoot_defender || { guarded: { attempts: 0, made: 0, pct: 0, share_of_cs: 0 }, unguarded: { attempts: 0, made: 0, pct: 0, share_of_cs: 0 } };
  const csClassified = cd.guarded.attempts + cd.unguarded.attempts;
  return (
    <div className="border border-white/10 bg-[#1c1c1c] p-4" data-testid="synergy-3p-mix">
      <div className="flex items-center gap-2 mb-3">
        <div className="font-heading text-lg text-white uppercase tracking-tight">3-Point Shot Mix</div>
        <div className="text-[10px] uppercase tracking-widest text-[#757575]">no-dribble vs dribble tags</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <MixBox label="Catch & Shoot" made={cs.made} attempts={cs.attempts} pct={cs.pct} share={cs.share_of_3pa} />
        <MixBox label="Off-Dribble" made={od.made} attempts={od.attempts} pct={od.pct} share={od.share_of_3pa} />
      </div>
      {/* Guarded vs Unguarded — subset of Catch & Shoot */}
      {cs.attempts > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0] mb-2 flex items-baseline gap-2">
            <span>Catch &amp; Shoot · Defender Split</span>
            {csClassified < cs.attempts && (
              <span className="text-[9px] text-[#575757] normal-case tracking-normal">
                ({cs.attempts - csClassified} of {cs.attempts} shots not tagged)
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <DefenderBox label="Unguarded" made={cd.unguarded.made} attempts={cd.unguarded.attempts} pct={cd.unguarded.pct} share={cd.unguarded.share_of_cs} />
            <DefenderBox label="Guarded"   made={cd.guarded.made}   attempts={cd.guarded.attempts}   pct={cd.guarded.pct}   share={cd.guarded.share_of_cs} contested />
          </div>
        </div>
      )}
      <div className="mt-4 text-[10px] uppercase tracking-widest text-[#575757]">
        Total 3PA: <span className="text-[var(--org-accent)] font-mono">{total}</span>
      </div>
    </div>
  );
}

function MixBox({ label, made, attempts, pct, share }) {
  return (
    <div className="border border-white/10 bg-[#101010] p-4">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0]">{label}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="font-heading text-4xl text-[var(--org-accent)] leading-none">{pct}%</div>
        <div className="text-xs text-[#a0a0a0] font-mono">{made}/{attempts}</div>
      </div>
      <div className="mt-3 text-[10px] uppercase tracking-widest text-[#757575]">
        Share of 3PA: <span className="text-white font-mono">{share}%</span>
      </div>
      <div className="h-1 mt-2 bg-white/5 relative overflow-hidden">
        <div className="absolute inset-y-0 left-0 bg-[var(--org-accent)]/70" style={{ width: `${share}%` }} />
      </div>
    </div>
  );
}

function DefenderBox({ label, made, attempts, pct, share, contested = false }) {
  return (
    <div className="border border-white/10 bg-[#101010] p-3">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0]">{label}</div>
        <div className={`text-[9px] uppercase tracking-widest ${contested ? "text-[#f87171]" : "text-[#4ade80]"}`}>
          {contested ? "Contested" : "Open"}
        </div>
      </div>
      <div className="mt-1.5 flex items-baseline gap-2">
        <div className="font-heading text-3xl text-white leading-none">{pct}%</div>
        <div className="text-xs text-[#a0a0a0] font-mono">{made}/{attempts}</div>
      </div>
      <div className="mt-2 text-[10px] uppercase tracking-widest text-[#575757]">
        Share of C&S: <span className="text-white font-mono">{share}%</span>
      </div>
      <div className="h-1 mt-1.5 bg-white/5 relative overflow-hidden">
        <div className={`absolute inset-y-0 left-0 ${contested ? "bg-[#f87171]/60" : "bg-[#4ade80]/60"}`} style={{ width: `${share}%` }} />
      </div>
    </div>
  );
}
